import { useCallback, useState, useEffect, useRef, MutableRefObject } from 'react';
import { Client, Conversation, Paginator, Message, User } from '@twilio/conversations';

import { ChatContext } from './context';
import { useSignalrConnection } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { selectPatient } from 'store/patient';
import { getFullName, noop } from 'utils';
import { useChatConversationsDeletion, useChatUnreadMessagesCounter } from './hooks';
import { selectPatients } from 'store/patientQueue';
import { HubPatient } from 'types';
import { useVideo } from 'hooks';
import { RemoteParticipant } from 'twilio-video';

const TWILIO_CONVERSATION_NOT_FOUND_ERROR_CODE = 50350;

type ChatProviderProps = {
  children?: JSX.Element;
};

export function ChatProvider({ children }: ChatProviderProps) {
  const { signalrConnectionId, getChatToken } = useSignalrConnection();
  const user = useAppSelector(selectUser);
  const patient = useAppSelector(selectPatient);
  const patients: HubPatient[] = useAppSelector(selectPatients);

  const prevPatients: MutableRefObject<HubPatient[]> = useRef<HubPatient[]>([]);

  const [client, setClient] = useState<Client | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [openedConversation, setOpenedConversation] = useState<Conversation | null>(null);
  const [openedRoomConversation, setOpenedRoomConversation] = useState<Conversation | null>(null);
  const [allConversations, setAllConversations] = useState<Conversation[]>([]);

  const { room } = useVideo();

  useChatConversationsDeletion({ allConversations });
  const { conversationsUnreadMessagesData, lastUpdatedChat } = useChatUnreadMessagesCounter({
    allConversations,
    openedConversation,
    openedRoomConversation,
  });

  useEffect(() => {
    if (signalrConnectionId && (user || patient)) {
      getChatToken(user?.clinicUser.id ?? signalrConnectionId).then((token) => {
        if (!token) {
          return;
        }
        const newClient = new Client(token);
        setClient(newClient);

        const userName = user ? getFullName(user) : patient!.userName;
        newClient.user.updateAttributes(userName);
      });
    }
  }, [signalrConnectionId, user, patient]);

  useEffect(() => {
    if (client) {
      const onInitialized = (): void => {
        setIsConnected(() => true);
        getAllUserConversations(client);
      };
      const onInitFailed = ({ error }: { error?: any }): void => {
        // eslint-disable-next-line no-console
        console.log(`Connection to twilio conversations failed with error: ${error!.message}`);
      };

      client.on('initialized', onInitialized);
      client.on('initFailed', onInitFailed);

      return () => {
        client.off('initialized', onInitialized);
        client.off('initFailed', onInitFailed);
      };
    }
  }, [client]);

  const startOrOpenRoomChat = useCallback(
    async (roomName: string) => {
      if (!isConnected || !client) {
        console.warn('⚠️ Not connected or client unavailable');
        return;
      }

      try {
        console.log(`🔍 Looking up group chat by uniqueName: ${roomName}`);
        const existingConversation = await client.getConversationByUniqueName(roomName);
        console.log(`✅ Group chat exists: ${existingConversation.sid}`);
        setOpenedRoomConversation(existingConversation);
        existingConversation.setAllMessagesRead();
      } catch (error: any) {
        if (error.body?.code === 50350) {
          try {
            const conversation = await client.createConversation({ uniqueName: roomName });
            console.log(`📦 Group Chat Created — SID: ${conversation.sid}, Name: ${roomName}`);

            const extractConnectionId = (identity: string): string => {
              if (!identity) return '';
              const parts = identity.split('|!##!|');
              return parts.length > 1 ? parts[0] : identity;
            };

            const participantConnectionIds: string[] =
              room && room.participants.size > 0
                ? [
                    extractConnectionId(client.user.identity),
                    ...Array.from(room.participants.values()).map((p) => extractConnectionId(p.identity)),
                  ]
                : [extractConnectionId(client.user.identity)];

            const uniqueIds = Array.from(new Set(participantConnectionIds.filter(Boolean)));

            const tryAddParticipant = async (id: string, retries = 3, delayMs = 500): Promise<void> => {
              for (let attempt = 1; attempt <= retries; attempt++) {
                try {
                  const userRecord = await client.getUser(id);
                  const raw = userRecord.attributes;

                  // 🧠 Safely coerce attributes to Record<string, string>
                  const attributesObj: Record<string, string> = {};

                  if (typeof raw === 'string') {
                    attributesObj.displayName = raw;
                  } else if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
                    for (const [key, value] of Object.entries(raw)) {
                      if (typeof value === 'string') {
                        attributesObj[key] = value;
                      }
                    }
                  }

                  // ✅ DO NOT JSON.stringify — pass plain object
                  const participant = await conversation.add(id, attributesObj);
                  console.log(`✅ Successfully added ${id} to group chat — Participant SID: ${participant.sid}`);
                  console.log(`👤 Attributes for ${id}:`, attributesObj);
                  return;
                } catch (err: any) {
                  const msg = err instanceof Error ? err.message : String(err);
                  console.warn(`⚠️ Attempt ${attempt} failed to add ${id} to group chat: ${msg}`);

                  if (attempt < retries) {
                    await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
                    console.log(`⏳ Retrying add(${id}) after ${delayMs * attempt}ms...`);
                  } else {
                    console.error(`❌ Final failure: Could not add ${id} to group chat after ${retries} attempts.`);
                  }
                }
              }
            };

            for (const id of uniqueIds) {
              await tryAddParticipant(id);
            }

            setOpenedRoomConversation(conversation);
          } catch (creationError) {
            console.error('❌ Failed to create group chat:', creationError);
          }
        } else {
          console.error('❌ Failed to access group chat:', error);
        }
      }
    },
    [client, isConnected, room],
  );

  useEffect(() => {
    if (!room || !client || !isConnected) return;

    const extractConnectionId = (identity: string): string => {
      const [id] = identity.split('|!##!|');
      return id;
    };

    const handleNewParticipant = async (participant: RemoteParticipant) => {
      const roomName = room.name;
      console.log(`🔄 New participant detected: ${participant.identity}`);

      try {
        const existingConversation = await client.getConversationByUniqueName(roomName);

        const tryAddParticipant = async (id: string, retries = 3, delayMs = 500): Promise<void> => {
          for (let attempt = 1; attempt <= retries; attempt++) {
            try {
              const userRecord = await client.getUser(id);
              let attrs = userRecord.attributes;
              if (typeof attrs === 'string') {
                attrs = { displayName: attrs };
              }
              const participant = await existingConversation.add(id, attrs);
              console.log(`✅ Successfully added ${id} to existing group chat — Participant SID: ${participant.sid}`);
              return;
            } catch (err: any) {
              const msg = err instanceof Error ? err.message : String(err);
              console.warn(`⚠️ Attempt ${attempt} failed to add ${id} to group chat: ${msg}`);
              if (attempt < retries) {
                await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
                console.log(`⏳ Retrying add(${id}) after ${delayMs * attempt}ms...`);
              } else {
                console.error(`❌ Final failure: Could not add ${id} after ${retries} attempts.`);
              }
            }
          }
        };

        const id = extractConnectionId(participant.identity);
        await tryAddParticipant(id);
      } catch (e) {
        console.warn('⚠️ Could not auto-add participant to chat — conversation might not exist yet.', e);
      }
    };

    room.on('participantConnected', handleNewParticipant);
    return () => {
      room.off('participantConnected', handleNewParticipant);
    };
  }, [room, client, isConnected]);

  useEffect(() => {
    if (openedConversation) {
      const onMessageAdded = (message: Message) => {
        message.conversation.setAllMessagesRead();
      };

      openedConversation.setAllMessagesRead();

      openedConversation.on('messageAdded', onMessageAdded);

      return () => {
        openedConversation.off('messageAdded', onMessageAdded);
      };
    }
  }, [openedConversation]);

  useEffect(() => {
    if (openedRoomConversation) {
      const onMessageAdded = (message: Message) => {
        message.conversation.setAllMessagesRead();
      };

      openedRoomConversation.setAllMessagesRead();

      openedRoomConversation.on('messageAdded', onMessageAdded);

      return () => {
        openedRoomConversation.off('messageAdded', onMessageAdded);
      };
    }
  }, [openedRoomConversation]);

  useEffect(() => {
    if (isConnected && (getFullName(user) || patient?.userName)) {
      const onConversationAdded = async (newConversation: Conversation) => {
        setAllConversations((prevAllConversations: Conversation[]) => [...prevAllConversations, newConversation]);
      };

      const onConversationRemoved = async (conversationToRemove: Conversation) => {
        setAllConversations((prevAllConversations: Conversation[]) => [
          ...prevAllConversations.filter((conversation: Conversation) => conversation.sid !== conversationToRemove.sid),
        ]);
      };

      client!.on('conversationAdded', onConversationAdded);
      client!.on('conversationLeft', onConversationRemoved);
      client!.on('conversationRemoved', onConversationRemoved);

      return () => {
        client!.off('conversationAdded', onConversationAdded);
        client!.off('conversationLeft', onConversationRemoved);
        client!.off('conversationRemoved', onConversationRemoved);
      };
    }
  }, [isConnected, user, patient]);

  useEffect(() => {
    if (
      openedConversation &&
      !allConversations.find((conversation: Conversation) => conversation.sid === openedConversation.sid)
    ) {
      setOpenedConversation(null);
    }
  }, [allConversations, openedConversation]);

  const getAllUserConversations = async (client: Client): Promise<void> => {
    const conversationsPaginator: Paginator<Conversation> = await client.getSubscribedConversations();
    const conversations: Conversation[] = await getAllItemsFromPaginator(conversationsPaginator);

    setAllConversations(conversations);
  };

  const openChat = useCallback(
    (providerId: string, patientId: string) => {
      if (!isConnected || !client) {
        throw new Error('It is not possible to open chat. The client did not connect');
      }

      const uniqueName = `$chat-${providerId}-${patientId}`;
      console.log(`🔍 Looking up 1:1 chat by uniqueName: ${uniqueName}`);

      client!
        .getConversationByUniqueName(uniqueName)
        .then((currentConversation: Conversation) => {
          setOpenedConversation(currentConversation);
          currentConversation.setAllMessagesRead();
        })
        .catch((error) => {
          if (error.body.code === TWILIO_CONVERSATION_NOT_FOUND_ERROR_CODE) {
            client!
              .createConversation({ uniqueName })
              .then(async (conversation: Conversation) => {
                console.log(`💬 1:1 Chat Created — SID: ${conversation.sid}, UniqueName: ${uniqueName}`);

                const tryAdd = async (id: string) => {
                  try {
                    const userRecord = await client!.getUser(id);
                    const raw = userRecord.attributes;
                    let attributesObj: Record<string, string> = {};

                    if (typeof raw === 'string') {
                      attributesObj = { displayName: raw };
                    } else if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
                      for (const [key, value] of Object.entries(raw)) {
                        if (typeof value === 'string') {
                          attributesObj[key] = value;
                        }
                      }
                    }

                    const participant = await conversation.add(id, attributesObj);
                    console.log(`✅ Added ${id} to 1:1 chat — Participant SID: ${participant.sid}`);
                  } catch (e) {
                    console.warn(`⚠️ Failed to add ${id} to 1:1 chat:`, e);
                  }
                };

                await tryAdd(patientId);
                await tryAdd(providerId);

                setOpenedConversation(conversation);
                conversation.setAllMessagesRead();
              })
              .catch((error) => {
                console.log(`❌ Create conversation failed with error: ${error}`);
              });
          }
        });
    },
    [isConnected, client],
  );

  const openChatByUniqueName = useCallback(
    (uniqueName: string) => {
      if (!isConnected) {
        throw new Error('It is not possible to open chat. The client did not connect');
      }

      client!
        .getConversationByUniqueName(uniqueName)
        .then((currentConversation: Conversation) => {
          setOpenedConversation(currentConversation);
          currentConversation.setAllMessagesRead();
        })
        .catch(noop);
    },
    [isConnected, client],
  );

  const openRoomChat = useCallback(
    (roomName: string) => {
      if (!isConnected) {
        throw new Error('It is not possible to open group chat. The client did not connect');
      }

      client!
        .getConversationByUniqueName(roomName)
        .then((currentConversation: Conversation) => {
          setOpenedRoomConversation(currentConversation);
          currentConversation.setAllMessagesRead();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(`Get conversations failed with error: ${error}`);
        });
    },
    [isConnected, client, user],
  );

  const addPatientToRoomChat = useCallback(
    (patientId: string, roomName: string) => {
      if (!isConnected) {
        throw new Error('It is not possible to add patient to group chat. The client did not connect');
      }

      if (!user) {
        throw new Error('Only provider has ability to add patient to room chat');
      }

      console.log(`🔍 Looking up fallback group chat by uniqueName: ${roomName}`);
      client!
        .getConversationByUniqueName(roomName)
        .then((currentConversation: Conversation) => {
          client!.getUser(patientId).then((user: User) => {
            currentConversation.add(patientId, user.attributes);
          });
        })
        .catch((error) => {
          // conversation does not exists, so we need to create
          if (error.body.code === TWILIO_CONVERSATION_NOT_FOUND_ERROR_CODE) {
            client!
              .createConversation({ uniqueName: roomName })
              .then((conversation: Conversation) => {
                console.log(
                  `📦 Group Chat Created via addPatientToRoomChat — SID: ${conversation.sid}, Name: ${roomName}`,
                );

                client!.getUser(client!.user.identity).then((user: User) => {
                  conversation.add(client!.user.identity, user.attributes);
                });
                client!.getUser(patientId).then((user: User) => {
                  conversation.add(patientId, user.attributes);
                });
              })
              .catch((error: any) => {
                // eslint-disable-next-line no-console
                console.log(`Create conversations failed with error: ${error}`);
              });
            // eslint-disable-next-line no-dupe-else-if
          }
        });
    },
    [isConnected, client, user],
  );

  const closeChat = (): void => {
    setOpenedConversation(null);
  };

  const closeRoomChat = (): void => {
    setOpenedRoomConversation(null);
  };

  const sendMessage = useCallback(
    (text: string) => {
      if (!isConnected) {
        throw new Error('It is not possible to open chat. The client did not connect');
      }

      if (!openedConversation) {
        throw new Error('It is not possible to send message. The chat did not open');
      }

      openedConversation.sendMessage(text);
    },
    [isConnected, client, openedConversation],
  );

  const sendMessageToRoomChat = useCallback(
    (text: string) => {
      if (!isConnected) {
        throw new Error('It is not possible to open chat. The client did not connect');
      }

      if (!openedRoomConversation) {
        throw new Error('It is not possible to send message. The chat did not open');
      }

      openedRoomConversation.sendMessage(text);
    },
    [isConnected, client, openedRoomConversation],
  );

  async function getAllItemsFromPaginator<T>(paginator: Paginator<T>): Promise<T[]> {
    let result: T[] = paginator.items;

    while (paginator.hasNextPage) {
      paginator = await paginator.nextPage();
      result = [...result, ...paginator.items];
    }

    return Promise.resolve(result);
  }

  // using for close chat when patient leave from waiting queue
  useEffect(() => {
    if (prevPatients.current !== patients && user) {
      const removedPatientsChatsNames: string[] = [];

      prevPatients.current.forEach((prevPatient: HubPatient) => {
        if (!patients.map((patient: HubPatient) => patient.connectionId).includes(prevPatient.connectionId)) {
          removedPatientsChatsNames.push(`$chat-${user.clinicUser.id}-${prevPatient.connectionId}`);
        }
      });

      if (removedPatientsChatsNames.includes(openedConversation?.uniqueName ?? '')) {
        setOpenedConversation(null);
      }
    }

    prevPatients.current = patients;
  }, [user, patients, openedConversation]);

  return (
    <ChatContext.Provider
      value={{
        conversation: openedConversation,
        roomConversation: openedRoomConversation,
        isConnected,
        identity: client?.user.identity ?? '',
        allConversations,
        conversationsUnreadMessagesData,
        lastUpdatedChat,
        openChat,
        openChatByUniqueName,
        openRoomChat,
        addPatientToRoomChat,
        closeChat,
        closeRoomChat,
        sendMessage,
        sendMessageToRoomChat,
        startOrOpenRoomChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
